"use client";

import Image from "next/image";
import {
  BookOpen,
  ChartLine,
  Globe,
  Hash,
  MailOpen,
  Megaphone,
  Rocket,
  Search,
  Star,
  Tags,
  TrendingUp,
  TriangleAlert,
  UserCheck,
  UserPen,
  Users,
  type LucideIcon,
} from "lucide-react";
import { ProcessLeadForm } from "@/components/ui/ProcessLeadForm";
import { Reveal } from "@/components/ui/Reveal";
import { downloadFile } from "@/lib/downloadFile";

const guideIconMap = {
  TriangleAlert,
  UserCheck,
  Users,
  Globe,
  Tags,
  UserPen,
  Star,
  TrendingUp,
  Search,
  Rocket,
  Hash,
  MailOpen,
  BookOpen,
  Megaphone,
  ChartLine,
} as const satisfies Record<string, LucideIcon>;

export type GuideIconName = keyof typeof guideIconMap;

export type GuideBullet = {
  iconName: GuideIconName;
  text: string;
};

export type GuideLeadSectionProps = {
  id?: string;
  heading: string;
  headingAccent?: string;
  headingSuffix?: string;
  description: string;
  bullets: ReadonlyArray<GuideBullet>;
  formId?: string;
  formHeading?: string;
  submitLabel?: string;
  className?: string;
  /** When set, submitting the form downloads this PDF (e.g. the guide it advertises). */
  downloadUrl?: string;
  downloadFileName?: string;
};

export function GuideLeadSection({
  id = "guide",
  heading,
  headingAccent,
  headingSuffix,
  description,
  bullets,
  formId = "guide-service-form",
  formHeading = "Is Your Manuscript The Next Bestseller?",
  submitLabel = "Submit Now",
  className,
  downloadUrl,
  downloadFileName,
}: GuideLeadSectionProps) {
  return (
    <section
      id={id}
      className={[
        "service-form-sec mb-8 overflow-x-clip bg-white py-12 sm:py-14 lg:mb-12 lg:py-[50px]",
        className,
      ]
        .filter(Boolean)
        .join(" ")}
    >
      <div className="mx-auto grid max-w-7xl items-center gap-10 px-4 sm:px-6 lg:grid-cols-2 lg:gap-12 lg:px-8">
        {/* Left — guide contents */}
        <div>
          <Reveal>
            <h2 className="font-heading text-[1.9rem] leading-[1.2] tracking-wide sm:text-4xl lg:text-[40px] lg:leading-[48px]">
              <span className="font-normal text-[#29361a]">{heading}</span>
              {headingAccent && (
                <>
                  {" "}
                  <span className="font-bold text-[#b46c29]">{headingAccent}</span>{" "}
                </>
              )}
              {headingSuffix && (
                <span className="font-normal text-[#29361a]">{headingSuffix}</span>
              )}
            </h2>
          </Reveal>

          <Reveal delayMs={120}>
            <p className="mt-4 text-[15px] leading-[1.8] text-[#333] sm:text-base">
              {description}
            </p>
          </Reveal>

          <ul className="mt-6 list-none space-y-3 p-0 sm:space-y-4">
            {bullets.map((bullet, index) => {
              const Icon = guideIconMap[bullet.iconName] ?? Star;
              return (
                <li key={bullet.text}>
                  <Reveal delayMs={160 + index * 60}>
                    <span className="flex items-center gap-2.5">
                      <Icon
                        className="h-5 w-5 shrink-0 text-[#b46c29]"
                        aria-hidden="true"
                      />
                      <span className="text-[15px] font-medium text-[#333] sm:text-base">
                        {bullet.text}
                      </span>
                    </span>
                  </Reveal>
                </li>
              );
            })}
          </ul>
        </div>

        {/* Right — green lead form panel with leaf (matches live .service-form) */}
        <Reveal variant="right" delayMs={200}>
          <div className="service-form service-form-contained">
            <Image
              src="/form-leaf.webp"
              alt=""
              width={255}
              height={600}
              className="service-form-leaf"
              aria-hidden="true"
            />
            <ProcessLeadForm
              id={formId}
              formHeading={formHeading}
              submitLabel={submitLabel}
              className="relative z-10 px-0 sm:px-2 lg:px-4"
              onSubmit={
                downloadUrl
                  ? () => downloadFile(downloadUrl, downloadFileName)
                  : undefined
              }
            />
          </div>
        </Reveal>
      </div>
    </section>
  );
}
